import React from 'react';
import {
  Button,
  VStack,
  Text,
  HStack,
  Badge,
  Table,
  Heading,
  Icon,
  Box,
  Link,
} from '@chakra-ui/react';
import { Building2, CreditCard, Users, Edit } from 'lucide-react';
import Drawer, { DrawerHeader, DrawerTitle, DrawerBody, DrawerFooter, DrawerCloseTrigger } from '@/components/ui/Drawer';
import withHashDrawer from '@/components/ui/withHashDrawer';
import LoadingSkeleton from '@/components/LoadingSkeleton';
import DetailsSection from '@/components/ui/DetailsSection';
import RelativeTime from '@/components/ui/RelativeTime';
import { BaseDetailsModalProps } from '@/types/DetailsModalTypes';
import { ClarityConnectionSection } from '@/entities/clarity/ClarityConnectionSection';
import { Organization, getOrganizationStatusColor, useGetOrganizationQuery, useGetOrganizationUsersQuery } from '../api';

interface OrganizationDetailsModalProps extends BaseDetailsModalProps {
  selectedOrganization?: Organization | null; // For backward compatibility
  isLoading?: boolean;
}

const OrganizationDetailsModal: React.FC<OrganizationDetailsModalProps> = ({
  isOpen,
  onClose,
  entityId,
  selectedOrganization,
  isLoading: externalLoading = false,
  navigateToModal,
}) => {
  // Fetch individual entity if entityId is provided (hash modal mode)
  const { 
    data: fetchedOrganization, 
    isLoading: isFetching, 
    error 
  } = useGetOrganizationQuery(entityId!, {
    skip: !entityId // Only fetch when we have an entityId
  });

  // Use fetched data or fallback to passed organization (backward compatibility)
  const organization = fetchedOrganization || selectedOrganization;
  const isLoading = entityId ? isFetching : externalLoading;

  // Fetch organization users
  const { 
    data: organizationUsers = [], 
    isLoading: isLoadingUsers 
  } = useGetOrganizationUsersQuery(organization?.id!, {
    skip: !organization?.id
  });

  const renderContent = () => {
    if (isLoading) {
      return <LoadingSkeleton />;
    }

    if (error) {
      return <Text color="red.500">Error loading organization details</Text>;
    }

    if (!organization) {
      return <Text>Organization not found</Text>;
    }

    return (
      <VStack gap={6} align="stretch">
            {/* Basic Information */}
            <DetailsSection
              title="Basic Information"
              icon={Building2}
              items={[
                {
                                label: "ID",
              value: organization.id
            },
            {
              label: "Name",
              value: organization.name
            },
            {
              label: "Status",
              value: organization.status,
              render: () => (
                <Badge 
                  size="sm" 
                  colorPalette={getOrganizationStatusColor(organization.status)}
                  variant="subtle"
                >
                  {organization.status}
                </Badge>
              )
            },
            {
              label: "Payment Provider",
              value: organization.payment_provider 
                ? (organization.payment_provider.display_name || organization.payment_provider.name || 'N/A')
                : 'N/A'
            },
            {
              label: "Created",
              value: organization.created_at ? "set" : null,
              render: () => <RelativeTime date={organization.created_at} />
            },
            {
              label: "Updated",
              value: organization.updated_at ? "set" : null,
              render: () => <RelativeTime date={organization.updated_at} fallbackDate={organization.created_at} />
            },
            {
              label: "Deleted",
              value: organization.deleted_at ? "set" : null,
              render: () => <RelativeTime date={organization.deleted_at!} />
            }
              ]}
            />

            <ClarityConnectionSection organizationId={organization.id} isOpen={isOpen} />

            {/* Subscription Information */}
            <DetailsSection
              title="Subscription Information"
              icon={CreditCard}
              hideEmptyFields={false}
              items={[
                {
                  label: "Trial Ends",
                  value: organization.trial_ends_at,
                  render: () => organization.trial_ends_at ? (
                    <RelativeTime date={organization.trial_ends_at} />
                  ) : (
                    <Text fontSize="sm" color="gray.500">N/A</Text>
                  )
                },
                {
                  label: "Subscription Ends",
                  value: organization.subscription_ends_at,
                  render: () => organization.subscription_ends_at ? (
                    <RelativeTime date={organization.subscription_ends_at} />
                  ) : (
                    <Text fontSize="sm" color="gray.500">N/A</Text>
                  )
                }
              ]}
            />

            {/* Users */}
            <Box 
              p={4} 
              borderWidth={1} 
              borderRadius="md" 
              borderColor="gray.200" 
              bg="white"
              _dark={{ 
                borderColor: "gray.600", 
                bg: "gray.700" 
              }}
            >
              <VStack align="start" gap={4}>
                <HStack>
                  <Icon as={Users} boxSize={4} color="brand.500" />
                  <Heading size="sm" color="brand.600" _dark={{ color: "brand.300" }}>
                    Users ({organizationUsers.length})
                  </Heading>
                </HStack>
                
                {isLoadingUsers ? (
                  <Text fontSize="sm" color="gray.500">Loading users...</Text>
                ) : organizationUsers.length > 0 ? (
                  <Table.Root size="sm" striped w="full">
                    <Table.Body>
                      {organizationUsers.map((user) => (
                        <Table.Row key={user.id}>
                          <Table.Cell>
                            <Link
                              href={`/users/#/${user.id}/view`}
                              color="brand.500"
                              fontSize="sm"
                              fontWeight="medium"
                              fontFamily="mono"
                            >
                              #{user.id}
                            </Link>
                          </Table.Cell>
                          <Table.Cell>
                            {user.first_name} {user.last_name}
                          </Table.Cell>
                          <Table.Cell>
                            {user.email}
                          </Table.Cell>
                          <Table.Cell>
                            {user.roles?.join(', ') || 'N/A'}
                          </Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table.Root>
                ) : (
                  <Text fontSize="sm" color="gray.500">No users found</Text>
                )}
              </VStack>
            </Box>
        </VStack>
      );
    };

    return (
      <Drawer
        isOpen={isOpen}
        onClose={onClose}
        size="lg"
        allowAgentChatInteraction
      >
        <DrawerHeader>
                  <DrawerTitle color="brand.700" _dark={{ color: "brand.300" }}>
          Organization Details {organization ? `- ${organization.name}` : ''}
        </DrawerTitle>
          <DrawerCloseTrigger />
        </DrawerHeader>
        
        <DrawerBody>
          {renderContent()}
        </DrawerBody>
        
        <DrawerFooter>
          <HStack justify="space-between" w="full">
            <HStack>
              {entityId && navigateToModal && (
                <Button 
                  variant="outline" 
                  onClick={() => navigateToModal(entityId, 'edit')}
                >
                  <Icon as={Edit} boxSize={4} mr={2} />
                  Edit
                </Button>
              )}
            </HStack>
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </HStack>
        </DrawerFooter>
      </Drawer>
    );
  };

// Export wrapped version for hash modal
export const OrganizationDetailsHashModal = withHashDrawer(OrganizationDetailsModal, { 
  action: 'view' 
});

// Default export for backward compatibility
export default OrganizationDetailsModal;
